import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import type { Ticket, TicketStatus, TicketPriority, TicketType } from '../../../core/models/Ticket';
import { TicketStatus as TicketStatusEnum, TicketPriority as TicketPriorityEnum, TicketType as TicketTypeEnum } from '../../../core/models/Ticket';

interface TicketDialogProps {
  open: boolean;
  onClose: () => void;
  ticket?: Ticket;
  projectId: string;
  projectKey?: string;
  onSubmit: (data: Omit<Ticket, 'id' | 'key' | 'lastSyncedAt' | 'created' | 'projectId'>) => Promise<void>;
}

export const TicketDialog: React.FC<TicketDialogProps> = ({
  open,
  onClose,
  ticket,
  projectId,
  projectKey,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Props reserved for future use
  void projectId;
  void projectKey;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      summary: ticket?.summary || '',
      description: ticket?.description || '',
      status: ticket?.status || TicketStatusEnum.TO_DO,
      priority: ticket?.priority || TicketPriorityEnum.MEDIUM,
      type: ticket?.type || TicketTypeEnum.TASK,
      url: ticket?.url || '',
    },
  });

  // Reset form when ticket changes
  useEffect(() => {
    if (open) {
      reset({
        summary: ticket?.summary || '',
        description: ticket?.description || '',
        status: ticket?.status || TicketStatusEnum.TO_DO,
        priority: ticket?.priority || TicketPriorityEnum.MEDIUM,
        type: ticket?.type || TicketTypeEnum.TASK,
        url: ticket?.url || '',
      });
    }
  }, [ticket, open, reset]);

  const handleFormSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Error submitting ticket:', error);
      alert('Failed to save ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusValue = watch('status');
  const priorityValue = watch('priority');
  const typeValue = watch('type');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ticket ? 'Edit Ticket' : 'Create Ticket'}</DialogTitle>
          <DialogDescription>
            {ticket
              ? `Edit ticket ${ticket.key}`
              : 'Create a new ticket for this project'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {ticket && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Ticket Key</label>
              <Input value={ticket.key} disabled className="bg-gray-50" />
            </div>
          )}

          <Input
            label="Summary"
            {...register('summary', { required: 'Summary is required' })}
            error={errors.summary?.message}
            placeholder="Enter ticket summary"
          />

          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea
              {...register('description')}
              rows={6}
              placeholder="Enter ticket description"
              className={errors.description ? 'border-red-500' : ''}
            />
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={statusValue}
                onValueChange={(value) => setValue('status', value as TicketStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TicketStatusEnum.TO_DO}>To Do</SelectItem>
                  <SelectItem value={TicketStatusEnum.IN_PROGRESS}>In Progress</SelectItem>
                  <SelectItem value={TicketStatusEnum.DONE}>Done</SelectItem>
                  <SelectItem value={TicketStatusEnum.RESOLVED}>Resolved</SelectItem>
                  <SelectItem value={TicketStatusEnum.CLOSED}>Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Priority</label>
              <Select
                value={priorityValue}
                onValueChange={(value) => setValue('priority', value as TicketPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TicketPriorityEnum.LOW}>Low</SelectItem>
                  <SelectItem value={TicketPriorityEnum.MEDIUM}>Medium</SelectItem>
                  <SelectItem value={TicketPriorityEnum.HIGH}>High</SelectItem>
                  <SelectItem value={TicketPriorityEnum.CRITICAL}>Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <Select
                value={typeValue}
                onValueChange={(value) => setValue('type', value as TicketType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TicketTypeEnum.TASK}>Task</SelectItem>
                  <SelectItem value={TicketTypeEnum.BUG}>Bug</SelectItem>
                  <SelectItem value={TicketTypeEnum.STORY}>Story</SelectItem>
                  <SelectItem value={TicketTypeEnum.EPIC}>Epic</SelectItem>
                  <SelectItem value={TicketTypeEnum.SUBTASK}>Subtask</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Input
            label="URL (Optional)"
            {...register('url')}
            error={errors.url?.message}
            placeholder="Enter ticket URL"
            type="url"
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {ticket ? 'Update Ticket' : 'Create Ticket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
